import { useEffect, useState } from "react";
import { Download, Loader2, RefreshCw, ScrollText, Send } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loader, PageHeader } from "@/components/common";
import { BriefEmailStatus } from "@/components/BriefEmailStatus";
import { useAuth } from "@/context/AuthContext";

const RGB = "var(--glow-admin)";
const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");
const mb = (n) => `${(n / 1024 / 1024).toFixed(1)} MB`;

export default function AdminPatentPacket() {
  const { session } = useAuth();
  const [m, setM] = useState(null);
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState(null);
  const load = () => api.get("/admin/patent-packet").then(({ data }) => setM(data)).catch((x) => setErr(errMsg(x)));
  useEffect(() => { load(); }, []);
  if (session?.user?.platform_role !== "super_admin") return <p className="text-q-muted" data-testid="patent-packet-forbidden">Only the super admin can open the patent packet.</p>;
  if (err) return <p className="text-red-300" role="alert" data-testid="patent-packet-error">{err}</p>;
  if (!m) return <Loader />;
  const run = async (kind, fn) => {
    setBusy(kind);
    try { await fn(); } catch (x) { toast.error(errMsg(x)); load(); } finally { setBusy(null); }
  };
  const regen = () => run("regen", async () => { setM((await api.post("/admin/patent-packet/regenerate")).data); toast.success("Patent packet rebuilt from the stored captures"); });
  const email = () => run("email", async () => { const { data } = await api.post("/admin/patent-packet/email"); setM(data); toast.success(data.email?.message || "Sent"); });
  const download = () => run("dl", async () => {
    const { data } = await api.get("/admin/patent-packet/download", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "The-Queue-Patent-Evidence-Packet.pdf"; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  });
  return (
    <div data-testid="patent-packet-page">
      <PageHeader eyebrow="Admin" title="Patent packet (PDF)" rgb={RGB} testid="patent-packet-title" />
      <section className="q-card p-5">
        <div className="flex items-start gap-4">
          <ScrollText className="mt-1 h-8 w-8 shrink-0 text-violet-300" />
          <div className="min-w-0">
            <p className="font-semibold text-q-text">Portal Connectivity and Data-Flow Workflow Evidence</p>
            <p className="text-sm text-q-muted">Real screenshots of every portal with the matching database record and timestamp at each step. Confidential: prepared for patent counsel.</p>
            <p className="mt-2 text-sm text-q-faint" data-testid="patent-packet-meta">
              {m.ready ? `Generated ${when(m.generated_at)} by ${m.generated_by} · ${m.pages} pages · ${mb(m.size)} · captured ${m.captured_at?.local || ""} · seed v${m.seed_version}` : "Not generated yet."}
            </p>
            <BriefEmailStatus e={m.email} onDownload={download} prefix="patent-packet" />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <button className="q-btn q-btn-primary min-h-[44px]" onClick={download} disabled={!m.ready || !!busy} data-testid="patent-packet-download-btn">
            {busy === "dl" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Download
          </button>
          <button className="q-btn q-btn-ghost min-h-[44px]" onClick={email} disabled={!m.ready || !!busy || !m.recipient} data-testid="patent-packet-email-btn">
            {busy === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Email to {m.recipient || "—"}
          </button>
          <button className="q-btn q-btn-secondary min-h-[44px]" onClick={regen} disabled={!!busy} data-testid="patent-packet-regenerate-btn">
            {busy === "regen" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Regenerate
          </button>
        </div>
        <p className="mt-4 text-xs text-q-faint" data-testid="patent-packet-recapture-note">Regenerate rebuilds the PDF from the stored captures. {m.recapture}</p>
      </section>
    </div>
  );
}
